import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/CreateEvent.css";
import { API_BASE_URL } from "../config";
import FormBuilderModal from "../components/FormBuilderModal";

const EVENT_TYPES_ENDPOINT = `${API_BASE_URL}/event_type`;

// Fallback event types in case API fails
const FALLBACK_EVENT_TYPES = [
  { id: 1, type: "Conference", icon: "fas fa-users" },
  { id: 2, type: "Meetup", icon: "fas fa-handshake" },
  { id: 3, type: "Exhibition", icon: "fas fa-landmark" },
  { id: 4, type: "Concert", icon: "fas fa-music" },
  { id: 5, type: "Marathon", icon: "fas fa-running" },
  { id: 6, type: "Other", icon: "fas fa-calendar-alt" },
];

// Icon mapping for event types
const EVENT_TYPE_ICONS = {
  conference: "fas fa-users",
  conferance: "fas fa-users",
  meetup: "fas fa-handshake",
  exhibition: "fas fa-landmark",
  expo: "fas fa-landmark",
  concert: "fas fa-music",
  marathon: "fas fa-running",
  event: "fas fa-calendar-alt",
  other: "fas fa-calendar-alt",
  default: "fas fa-calendar",
};

// Description mapping for event types
const EVENT_TYPE_DESCRIPTIONS = {
  conference: "Professional gathering for knowledge sharing",
  conferance: "Professional gathering for knowledge sharing",
  meetup: "Casual gathering for networking",
  exhibition: "Showcase of products and innovations",
  expo: "Showcase of products and innovations",
  concert: "Live music performance event",
  marathon: "Long-distance running competition",
  event: "Custom event type",
  other: "Custom event type",
};

const CITIES = [
  "Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai",
  "Kolkata", "Pune", "Ahmedabad", "Jaipur", "Surat",
  "Other", "Lucknow", "Nagpur", "Indore", "Bhopal",
  "Visakhapatnam", "Patna", "Vadodara", "Ghaziabad", "Ludhiana",
  "Agra", "Nashik", "Faridabad", "Meerut", "Rajkot",
  "Varanasi", "Srinagar", "Aurangabad", "Amritsar", "Coimbatore",
  "Jabalpur", "Gwalior", "Vijayawada", "Jodhpur", "Madurai",
  "Raipur", "Kota", "Guwahati", "Chandigarh", "Solapur",
  "Thane", "Allahabad", "Ranchi", "Gurgaon", "Bhubaneswar",
  "Kochi", "Jamshedpur", "Dehradun", "Mysore", "Jalandhar", "Bareilly",
];

// Local storage keys
const STORAGE_KEYS = {
  EVENT_FORM: "eventfork_event_form_data",
  CONFIG_DATA: "eventfork_config_data",
};

// Default custom form fields
const loadDefaultCustomFormFields = () => {
  try {
    const savedSchema = localStorage.getItem("eventfork_registration_default_schema");
    if (savedSchema) {
      const parsed = JSON.parse(savedSchema);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((field) => ({
          label: field.label,
          field_type: getFieldTypeMapping(field.type),
          is_required: field.required || false,
          place_holder: field.placeholder || "",
          options: field.type === "select" || field.type === "radio" ? field.options : [],
        }));
      }
    }
  } catch (e) {
    console.error("Failed to load custom form schema", e);
  }
  return [];
};

const getFieldTypeMapping = (type) => {
  const mapping = {
    text: "TextInput",
    email: "EmailInput",
    tel: "PhoneInput",
    number: "NumberInput",
    textarea: "TextArea",
    select: "Dropdown",
    radio: "RadioGroup",
    checkbox: "Checkbox",
    date: "DatePicker",
    time: "TimePicker",
  };
  return mapping[type] || "TextInput";
};

const DEFAULT_ATTENDEE_FORM_FIELDS = [
  {
    label: "Full Name",
    field_type: "TextInput",
    is_required: true,
    place_holder: "Enter your full name",
    options: [],
  },
  {
    label: "Email Address",
    field_type: "EmailInput",
    is_required: true,
    place_holder: "your.email@example.com",
    options: [],
  },
  {
    label: "Phone Number",
    field_type: "PhoneInput",
    is_required: false,
    place_holder: "+91 98765 43210",
    options: [],
  },
  {
    label: "Organization",
    field_type: "TextInput",
    is_required: false,
    place_holder: "Enter your organization",
    options: [],
  },
];

const DEFAULT_SPEAKER_FORM_FIELDS = [
  {
    label: "Full Name",
    field_type: "TextInput",
    is_required: true,
    place_holder: "Enter your full name",
    options: [],
  },
  {
    label: "Email Address",
    field_type: "EmailInput",
    is_required: true,
    place_holder: "Enter your email address",
    options: [],
  },
  {
    label: "Talk Topic",
    field_type: "TextInput",
    is_required: true,
    place_holder: "Enter your talk topic",
    options: [],
  },
  {
    label: "Talk Details",
    field_type: "TextArea",
    is_required: true,
    place_holder: "Describe your talk",
    options: [],
  },
];

const CreateEvent = () => {
  const loadFromStorage = () => {
    try {
      const savedFormData = localStorage.getItem(STORAGE_KEYS.EVENT_FORM);
      if (savedFormData) {
        return JSON.parse(savedFormData);
      }
    } catch (error) {
      console.error("Error loading from storage:", error);
    }
    return null;
  };

  const [formData, setFormData] = useState(() => {
    const saved = loadFromStorage();
    return saved?.formData || {
      event_type_id: "",
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      venue: "",
      city: "",
      organization_name: "",
      is_paid: false,
      ticket_price: "",
      contact_email: "",
      contact_phone: "",
      github_url: "",
      twitter_url: "",
      linkedin_url: "",
      instagram_url: "",
      facebook_url: "",
      website_url: "",
    };
  });

  const [eventTypes, setEventTypes] = useState(FALLBACK_EVENT_TYPES);
  const [loadingEventTypes, setLoadingEventTypes] = useState(true);
  const [eventTypesError, setEventTypesError] = useState("");
  const [selectedEventTypeId, setSelectedEventTypeId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [banner, setBanner] = useState(null);
  const [logo, setLogo] = useState(null);
  const [showFormBuilderModal, setShowFormBuilderModal] = useState(false);
  const [sponsorCategories, setSponsorCategories] = useState([]);
  const [loadingSponsorCategories, setLoadingSponsorCategories] = useState(false);
  const [newSponsorCategory, setNewSponsorCategory] = useState("");
  const [bannerPreview, setBannerPreview] = useState(() => {
    const saved = loadFromStorage();
    return saved?.bannerPreview || null;
  });
  const [logoPreview, setLogoPreview] = useState(() => {
    const saved = loadFromStorage();
    return saved?.logoPreview || null;
  });
  const [activeTab, setActiveTab] = useState("attendee");
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = loadFromStorage();
    return saved?.currentStep || 1;
  });

  const [enableAttendee, setEnableAttendee] = useState(true);
  const [enableSpeaker, setEnableSpeaker] = useState(false);
  const [enableSponsor, setEnableSponsor] = useState(false);
  const [enableExhibitor, setEnableExhibitor] = useState(false);

  const [attendeeFields, setAttendeeFields] = useState(() => {
    const saved = loadFromStorage();
    const defaultFields = loadDefaultCustomFormFields();
    return saved?.attendeeFields || {
      registration_start_date: "",
      registration_end_date: "",
      registration_limit: "",
      is_auto_approve: true,
      registration_form_type: "default",
      custom_form_fields: defaultFields,
    };
  });

  const [speakerFields, setSpeakerFields] = useState(() => {
    const saved = loadFromStorage();
    const defaultFields = loadDefaultCustomFormFields();
    return saved?.speakerFields || {
      speaker_start_date: "",
      speaker_end_date: "",
      speaker_limit: "",
      speaker_approval: "auto",
      speaker_form_type: "default",
      custom_speaker_form: defaultFields,
    };
  });

  const [sponsorFields, setSponsorFields] = useState(() => {
    const saved = loadFromStorage();
    return saved?.sponsorFields || {
      sponsor_names: [],
      sponsor_logos: [],
      sponsor_categories: [],
    };
  });

  const [exhibitorFields, setExhibitorFields] = useState(() => {
    const saved = loadFromStorage();
    return saved?.exhibitorFields || {
      exhibitor_names: [],
      exhibitor_logos: [],
    };
  });

  const [newSponsorName, setNewSponsorName] = useState("");
  const [newSponsorLogo, setNewSponsorLogo] = useState(null);
  const [newExhibitorName, setNewExhibitorName] = useState("");
  const [newExhibitorLogo, setNewExhibitorLogo] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [usingFallbackData, setUsingFallbackData] = useState(false);
  const [showDefaultFormPreview, setShowDefaultFormPreview] = useState(false);
  const [customFormType, setCustomFormType] = useState("attendee");
  const navigate = useNavigate();

  // Save state to localStorage
  useEffect(() => {
    const saveToStorage = () => {
      try {
        const dataToSave = {
          formData,
          currentStep,
          attendeeFields,
          speakerFields,
          sponsorFields,
          exhibitorFields,
          enableAttendee,
          enableSpeaker,
          enableSponsor,
          enableExhibitor,
          bannerPreview,
          logoPreview,
          selectedEventTypeId,
          selectedTemplate,
        };
        localStorage.setItem(STORAGE_KEYS.EVENT_FORM, JSON.stringify(dataToSave));
      } catch (error) {
        console.error("Error saving to storage:", error);
      }
    };
    saveToStorage();
  }, [
    formData, currentStep, attendeeFields, speakerFields, sponsorFields,
    exhibitorFields, enableAttendee, enableSpeaker, enableSponsor,
    enableExhibitor, bannerPreview, logoPreview, selectedEventTypeId,
    selectedTemplate,
  ]);

  const clearSavedData = () => {
    localStorage.removeItem(STORAGE_KEYS.EVENT_FORM);
    localStorage.removeItem(STORAGE_KEYS.CONFIG_DATA);
  };

  // Fetch event types from API
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        setLoadingEventTypes(true);
        setEventTypesError("");
        const response = await fetch(EVENT_TYPES_ENDPOINT);
        if (response.ok) {
          const apiEventTypes = await response.json();
          if (Array.isArray(apiEventTypes) && apiEventTypes.length > 0) {
            const transformedTypes = apiEventTypes.map((type) => ({
              ...type,
              icon: getEventTypeIcon(type.type.toLowerCase()),
            }));
            setEventTypes(transformedTypes);
            setUsingFallbackData(false);
          } else {
            throw new Error("No event types returned from API");
          }
        } else {
          throw new Error(`API error: ${response.status}`);
        }
      } catch (err) {
        console.error("Error loading event types:", err);
        setEventTypesError("Unable to load event types. Using default options.");
        setEventTypes(FALLBACK_EVENT_TYPES);
        setUsingFallbackData(true);
      } finally {
        setLoadingEventTypes(false);
      }
    };
    fetchEventTypes();
  }, []);

  useEffect(() => {
    const fetchSponsorCategories = async () => {
      try {
        setLoadingSponsorCategories(true);
        const res = await fetch(`${API_BASE_URL}/sponsers_categories`);
        if (!res.ok) throw new Error("Failed to fetch sponsor categories");
        const data = await res.json();
        setSponsorCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Sponsor category fetch error:", err);
        setSponsorCategories([]);
      } finally {
        setLoadingSponsorCategories(false);
      }
    };
    fetchSponsorCategories();
  }, []);

  useEffect(() => {
    const loadSavedCustomForms = () => {
      try {
        const savedSpeakerForm = localStorage.getItem('eventfork_custom_form_data_speaker');
        if (savedSpeakerForm) {
          const formData = JSON.parse(savedSpeakerForm);
          if (formData.type === 'speaker' && formData.fields && formData.fields.length > 0) {
            setSpeakerFields(prev => ({
              ...prev,
              custom_speaker_form: formData.fields,
              speaker_form_type: 'custom'
            }));
          }
        }
        const savedAttendeeForm = localStorage.getItem('eventfork_custom_form_data_attendee');
        if (savedAttendeeForm) {
          const formData = JSON.parse(savedAttendeeForm);
          if (formData.type === 'attendee' && formData.fields && formData.fields.length > 0) {
            setAttendeeFields(prev => ({
              ...prev,
              custom_form_fields: formData.fields,
              registration_form_type: 'custom'
            }));
          }
        }
      } catch (e) {
        console.error('Failed to load custom forms', e);
      }
    };
    loadSavedCustomForms();
  }, []);

  const getEventTypeIcon = (typeName) => {
    const normalizedName = typeName.toLowerCase();
    return EVENT_TYPE_ICONS[normalizedName] || EVENT_TYPE_ICONS.default;
  };

  const getEventTypeDescription = (typeName) => {
    const normalizedName = typeName.toLowerCase();
    return EVENT_TYPE_DESCRIPTIONS[normalizedName] || "Custom event type";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const formatDateForApi = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const handleEventTypeCardClick = (typeObj) => {
    setSelectedEventTypeId(typeObj.id);
    setSelectedTemplate(typeObj.template || null);
    setFormData((prev) => ({
      ...prev,
      event_type_id: typeObj.id,
    }));
    if (typeObj.template?.event_config?.speaker_required) {
      setEnableSpeaker(true);
    }
    if (typeObj.template?.event_config?.sponser_required) {
      setEnableSponsor(true);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`Banner image is too large. Please select an image smaller than 5MB.`);
        e.target.value = "";
        setBanner(null);
        setBannerPreview(null);
        return;
      }
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        alert("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
        e.target.value = "";
        setBanner(null);
        setBannerPreview(null);
        return;
      }
      setBanner(file);
      const previewUrl = URL.createObjectURL(file);
      setBannerPreview(previewUrl);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`Logo image is too large. Please select an image smaller than 5MB.`);
        e.target.value = "";
        setLogo(null);
        setLogoPreview(null);
        return;
      }
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        alert("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
        e.target.value = "";
        setLogo(null);
        setLogoPreview(null);
        return;
      }
      setLogo(file);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const handleAttendeeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAttendeeFields((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSpeakerChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSpeakerFields((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addSponsor = () => {
    if (!newSponsorName.trim()) {
      alert("Sponsor name is required");
      return;
    }
    if (!newSponsorLogo) {
      alert("Sponsor logo is required");
      return;
    }
    if (!newSponsorCategory) {
      alert("Sponsor category is required");
      return;
    }
    setSponsorFields((prev) => ({
      ...prev,
      sponsor_names: [...prev.sponsor_names, newSponsorName],
      sponsor_logos: [...prev.sponsor_logos, newSponsorLogo],
      sponsor_categories: [...prev.sponsor_categories, newSponsorCategory],
    }));
    setNewSponsorName("");
    setNewSponsorLogo(null);
    setNewSponsorCategory("");
  };

  const removeSponsor = (index) => {
    setSponsorFields((prev) => ({
      ...prev,
      sponsor_names: prev.sponsor_names.filter((_, i) => i !== index),
      sponsor_logos: prev.sponsor_logos.filter((_, i) => i !== index),
      sponsor_categories: prev.sponsor_categories.filter((_, i) => i !== index),
    }));
  };

  const addExhibitor = () => {
    if (!newExhibitorName.trim()) {
      alert("Exhibitor name is required");
      return;
    }
    if (!newExhibitorLogo) {
      alert("Exhibitor logo is required");
      return;
    }
    setExhibitorFields((prev) => ({
      ...prev,
      exhibitor_names: [...prev.exhibitor_names, newExhibitorName],
      exhibitor_logos: [...prev.exhibitor_logos, newExhibitorLogo],
    }));
    setNewExhibitorName("");
    setNewExhibitorLogo(null);
  };

  const removeExhibitor = (index) => {
    setExhibitorFields((prev) => ({
      ...prev,
      exhibitor_names: prev.exhibitor_names.filter((_, i) => i !== index),
      exhibitor_logos: prev.exhibitor_logos.filter((_, i) => i !== index),
    }));
  };

  const handleSponsorLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`Logo is too large. Please select an image smaller than 5MB.`);
        return;
      }
      setNewSponsorLogo(file);
    }
  };

  const handleExhibitorLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`Logo is too large. Please select an image smaller than 5MB.`);
        return;
      }
      setNewExhibitorLogo(file);
    }
  };


  const formatDateTimeForApi = (value) => {
    if (!value) return "";

    // 2026-10-05T14:30 → 2026-10-05 14:30:00
    return value.replace("T", " ") + ":00";
  };
  const prepareFormData = () => {
    const userStr = localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;
    const token = localStorage.getItem("token");

    if (!user) {
      throw new Error("Please sign in to create events");
    }

    const payload = new FormData();

    payload.append("event_type_id", formData.event_type_id);
    payload.append("name", formData.name);
    payload.append("description", formData.description || "");
    payload.append(
      "organization_name",
      formData.organization_name?.trim() || ""
    );


    payload.append("orgorganization_emailid", formData.contact_email || "");
    payload.append("orgorganization_contact", formData.contact_phone || "");
    payload.append("orgorganization_github", formData.github_url || "");
    payload.append("orgorganization_twitter", formData.twitter_url || "");
    payload.append("orgorganization_linkedin", formData.linkedin_url || "");
    payload.append("orgorganization_instagram", formData.instagram_url || "");
    payload.append("orgorganization_facebook", formData.facebook_url || "");
    payload.append("orgorganization_website", formData.website_url || "");
    payload.append("start_date", formatDateTimeForApi(formData.start_date));
    payload.append("end_date", formatDateTimeForApi(formData.end_date));
    payload.append("venue", formData.venue);
    payload.append("city", formData.city);
    payload.append("is_paid", String(formData.is_paid));



    if (formData.is_paid && formData.ticket_price) {
      payload.append("ticket_price", String(formData.ticket_price));
    }

    payload.append("isSpeaker", String(enableSpeaker));
    payload.append("isSponser", String(enableSponsor));
    payload.append("isExibitor", String(enableExhibitor));

    if (enableAttendee) {
      if (attendeeFields.registration_start_date) {
        payload.append("registration_start_date", formatDateForApi(attendeeFields.registration_start_date));
      }
      if (attendeeFields.registration_end_date) {
        payload.append("registration_end_date", formatDateForApi(attendeeFields.registration_end_date));
      }
      if (attendeeFields.registration_limit) {
        payload.append("registration_limit", String(attendeeFields.registration_limit));
      }

      const attendeeConfig = {
        start_date: formatDateForApi(attendeeFields.registration_start_date),
        end_date: formatDateForApi(attendeeFields.registration_end_date),
        custom_form: attendeeFields.registration_form_type === "custom"
          ? attendeeFields.custom_form_fields
          : DEFAULT_ATTENDEE_FORM_FIELDS,
        default_attendee_limit: attendeeFields.registration_limit || 1000,
        registration_form_type: attendeeFields.registration_form_type,
        is_auto_approve: attendeeFields.is_auto_approve,
      };
      payload.append("attendeeConfig", JSON.stringify(attendeeConfig));
    }

    if (enableSpeaker) {
      const speakerConfig = {
        start_date: formatDateForApi(speakerFields.speaker_start_date),
        end_date: formatDateForApi(speakerFields.speaker_end_date),
        custom_form: speakerFields.speaker_form_type === "custom"
          ? speakerFields.custom_speaker_form
          : DEFAULT_SPEAKER_FORM_FIELDS,
        speaker_limit: speakerFields.speaker_limit || 50,
        speaker_form_type: speakerFields.speaker_form_type,
      };
      payload.append("speakerConfig", JSON.stringify(speakerConfig));
    }

    if (enableSponsor) {
      sponsorFields.sponsor_names.forEach((name) => {
        payload.append("sponsorNames", name);
      });
      sponsorFields.sponsor_logos.forEach((logo) => {
        payload.append("sponsorLogos", logo);
      });
      sponsorFields.sponsor_categories.forEach((cat) => {
        payload.append("sponsercategory", cat);
      });
    }

    if (enableExhibitor) {
      exhibitorFields.exhibitor_names.forEach((name) => {
        payload.append("exibitorNames", name);
      });
      exhibitorFields.exhibitor_logos.forEach((logo) => {
        payload.append("exibitorLogos", logo);
      });
    }

    if (banner) {
      payload.append("bannerfile", banner);
    }
    if (logo) {
      payload.append("bannerlog", logo);
    }

    return { payload, token };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {

      // Attendee validation
      if (enableAttendee) {
        if (!attendeeFields.registration_start_date) {
          setError("Please select Attendee Registration Start Date and end Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          setLoading(false);
          return;
        }

        if (!attendeeFields.registration_end_date) {
          setError("Please select Attendee Registration End Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          setLoading(false);
          return;
        }
      }

      // Speaker validation
      if (enableSpeaker) {
        if (!speakerFields.speaker_start_date) {
          setError("Please select Speaker Registration Start Date and End Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          setLoading(false);
          return;
        }

        if (!speakerFields.speaker_end_date) {
          setError("Please select Speaker Registration End Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          setLoading(false);
          return;
        }
      }


      const { payload, token } = prepareFormData();

      const response = await fetch(`${API_BASE_URL}/event`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: payload,
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || `Failed to create event: ${response.status}`);
      }

      clearSavedData();
      if (bannerPreview) URL.revokeObjectURL(bannerPreview);
      if (logoPreview) URL.revokeObjectURL(logoPreview);

      navigate("/dashboard#my-events");
    } catch (err) {
      console.error("Create event error:", err);
      if (err.message.includes("413")) {
        setError("File uploads are too large. Please use smaller images.");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel? All unsaved changes will be lost.")) {
      clearSavedData();
      if (bannerPreview) URL.revokeObjectURL(bannerPreview);
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      navigate("/");
    }
  };

  const handleNextStep = () => {
    // Step 1 Validation
    if (currentStep === 1) {
      if (
        !formData.event_type_id ||
        !formData.name ||
        !formData.start_date ||
        !formData.end_date ||
        !formData.venue ||
        !formData.city ||
        !formData.organization_name?.trim() ||
        !formData.contact_email?.trim()
      ) {
        setError("Please fill in all required fields.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }

    // Step 2 Validation
    if (currentStep === 2) {
      if (enableAttendee) {
        if (!attendeeFields.registration_start_date) {
          setError("Please select Registration Start Date & End Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }

        if (!attendeeFields.registration_end_date) {
          setError("Please select Registration End Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
      }

      if (enableSpeaker) {
        if (!speakerFields.speaker_start_date) {
          setError("Please select Speaker Start Date & End Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }

        if (!speakerFields.speaker_end_date) {
          setError("Please select Speaker End Date.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
      }
    }

    setError("");
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const getSelectedEventTypeName = () => {
    const selectedType = eventTypes.find((type) => type.id === formData.event_type_id);
    return selectedType ? selectedType.type : "Not selected";
  };

  const handleOpenCustomFormBuilder = (type) => {
    setCustomFormType(type);
    setShowFormBuilderModal(true);
  };

  const handleFormBuilderSave = (formType, customFormFields) => {
    console.log(`Saving ${formType} form with ${customFormFields.length} fields`);

    if (formType === "attendee") {
      setAttendeeFields((prev) => ({
        ...prev,
        registration_form_type: "custom",
        custom_form_fields: customFormFields,
      }));
    } else if (formType === "speaker") {
      setSpeakerFields((prev) => ({
        ...prev,
        speaker_form_type: "custom",
        custom_speaker_form: customFormFields,
      }));
    }

    alert(`${formType === 'speaker' ? 'Speaker' : 'Attendee'} custom form saved successfully!`);
  };

  // Render event type cards
  const renderEventTypeCards = () => {
    if (loadingEventTypes) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading event types...</p>
        </div>
      );
    }

    return eventTypes.map((type) => (
      <div
        key={type.id}
        className={`event-type-card ${selectedEventTypeId === type.id ? "selected" : ""}`}
        onClick={() => handleEventTypeCardClick(type)}
      >
        <div className="event-type-icon">
          <i className={type.icon || EVENT_TYPE_ICONS.default} />
        </div>
        <div className="event-type-content">
          <h4 className="event-type-name">{type.type}</h4>
          <p className="event-type-desc">{getEventTypeDescription(type.type)}</p>
          {usingFallbackData && (
            <span className="data-source-badge">Default</span>
          )}
        </div>
      </div>
    ));
  };

  // Render default form preview
  const renderDefaultFormPreview = () => {
    const isSpeakerForm = activeTab === "speaker";
    const title = isSpeakerForm
      ? "Speaker Registration Form Preview"
      : "Attendee Registration Form Preview";

    const hasCustomForm = isSpeakerForm
      ? speakerFields.speaker_form_type === "custom" && speakerFields.custom_speaker_form.length > 0
      : attendeeFields.registration_form_type === "custom" && attendeeFields.custom_form_fields.length > 0;

    const customFields = isSpeakerForm
      ? speakerFields.custom_speaker_form
      : attendeeFields.custom_form_fields;

    const renderCustomFieldPreview = (field, index) => {
      const label = field.label || `Field ${index + 1}`;
      switch (field.field_type) {
        case "TextArea":
          return (
            <div className="form-group" key={index}>
              <label>{label} {field.is_required && <span className="required">*</span>}</label>
              <textarea className="form-input" placeholder={field.place_holder || ""} disabled rows="3"></textarea>
            </div>
          );
        case "Dropdown":
          return (
            <div className="form-group" key={index}>
              <label>{label} {field.is_required && <span className="required">*</span>}</label>
              <select className="form-select" disabled>
                <option>{field.place_holder || "Select an option"}</option>
                {(field.options || []).map((opt, i) => (
                  <option key={i}>{opt}</option>
                ))}
              </select>
            </div>
          );
        case "RadioGroup":
          return (
            <div className="form-group" key={index}>
              <label>{label} {field.is_required && <span className="required">*</span>}</label>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                {(field.options || []).map((opt, i) => (
                  <label key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "normal", cursor: "default" }}>
                    <input type="radio" disabled /> {opt}
                  </label>
                ))}
              </div>
            </div>
          );
        case "Checkbox":
          return (
            <div className="form-group" key={index}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" disabled />
                {label} {field.is_required && <span className="required">*</span>}
              </label>
            </div>
          );
        default:
          const inputType = field.field_type === "EmailInput" ? "email"
            : field.field_type === "PhoneInput" ? "tel"
              : field.field_type === "NumberInput" ? "number"
                : field.field_type === "DatePicker" ? "date"
                  : "text";
          return (
            <div className="form-group" key={index}>
              <label>{label} {field.is_required && <span className="required">*</span>}</label>
              <input type={inputType} className="form-input" placeholder={field.place_holder || ""} disabled />
            </div>
          );
      }
    };

    return (
      <div className="modal-overlay">
        <div className="modal-content form-preview-modal">
          <div className="modal-header">
            <h3>{title}</h3>
            <button type="button" className="close-modal" onClick={() => setShowDefaultFormPreview(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="form-preview">
              <h4>{hasCustomForm ? "Custom Registration Form" : "Default Registration Form"}</h4>
              <div className="preview-form">
                {hasCustomForm ? (
                  <>
                    {customFields.map((field, index) => renderCustomFieldPreview(field, index))}
                    <button type="button" className="btn btn-primary" disabled>
                      {isSpeakerForm ? "Submit Proposal" : "Register Now"}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Full Name <span className="required">*</span></label>
                      <input type="text" className="form-input" placeholder="Enter your full name" disabled />
                    </div>
                    <div className="form-group">
                      <label>Email Address <span className="required">*</span></label>
                      <input type="email" className="form-input" placeholder="Enter your email address" disabled />
                    </div>
                    {isSpeakerForm ? (
                      <>
                        <div className="form-group">
                          <label>Talk Topic <span className="required">*</span></label>
                          <input type="text" className="form-input" placeholder="Enter your talk topic" disabled />
                        </div>
                        <div className="form-group">
                          <label>Talk Details <span className="required">*</span></label>
                          <textarea className="form-input" placeholder="Describe your talk" disabled rows="4"></textarea>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>Phone Number</label>
                          <input type="tel" className="form-input" placeholder="Enter your phone number" disabled />
                        </div>
                        <div className="form-group">
                          <label>Organization</label>
                          <input type="text" className="form-input" placeholder="Enter your organization" disabled />
                        </div>
                      </>
                    )}
                    <button type="button" className="btn btn-primary" disabled>
                      {isSpeakerForm ? "Submit Proposal" : "Register Now"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render sponsor management section
  const renderSponsorManagement = () => (
    <div className="sponsor-management">
      <div className="section-header">
        <h4>Sponsor Management</h4>
        <p>Add sponsors with their details</p>
      </div>

      <div className="sponsor-form">
        <div className="form-group">
          <label>Sponsor Name <span className="required">*</span></label>
          <input
            type="text"
            className="form-input"
            value={newSponsorName}
            onChange={(e) => setNewSponsorName(e.target.value)}
            placeholder="Enter sponsor name"
          />
        </div>

        <div className="form-group">
          <label>Sponsor Category <span className="required">*</span></label>
          <select
            className="form-select"
            value={newSponsorCategory}
            onChange={(e) => setNewSponsorCategory(e.target.value)}
          >
            <option value="">Select Category</option>
            {sponsorCategories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Sponsor Logo <span className="required">*</span></label>
          <div
            className="file-upload-card"
            onClick={() => document.getElementById("sponsorLogo").click()}
          >
            <div className="file-upload-icon">
              <i className="fas fa-camera" />
            </div>
            <div className="file-upload-content">
              <div className="file-upload-title">Upload Logo</div>
              <div className="file-upload-subtitle">Optional logo image</div>
            </div>
          </div>
          <input
            type="file"
            id="sponsorLogo"
            name="sponsorLogo"
            onChange={handleSponsorLogoChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          {newSponsorLogo && (
            <div className="file-preview">
              <span>{newSponsorLogo.name}</span>
              <button
                type="button"
                className="remove-file-btn"
                onClick={() => setNewSponsorLogo(null)}
              >
                <i className="fas fa-trash" />
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={addSponsor}
          disabled={!newSponsorName.trim()}
        >
          <i className="fas fa-plus" /> Add Sponsor
        </button>
      </div>

      <div className="sponsor-list">
        <h5>Added Sponsors ({sponsorFields.sponsor_names.length})</h5>
        {sponsorFields.sponsor_names.length === 0 ? (
          <p className="empty-message">No sponsors added yet.</p>
        ) : (
          <div className="sponsor-items">
            {sponsorFields.sponsor_names.map((sponsor, index) => (
              <div key={index} className="sponsor-item">
                <div className="sponsor-info">
                  <span className="sponsor-name">{sponsor}</span>
                  <span className="sponsor-files">
                    {sponsorFields.sponsor_logos[index] && (
                      <i className="fas fa-camera" title="Has logo" />
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  className="remove-sponsor-btn"
                  onClick={() => removeSponsor(index)}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render exhibitor management section
  const renderExhibitorManagement = () => (
    <div className="exhibitor-management">
      <div className="section-header">
        <h4>Exhibitor Management</h4>
        <p>Add exhibitors with their details</p>
      </div>

      <div className="exhibitor-form">
        <div className="form-group">
          <label>Exhibitor Name <span className="required">*</span></label>
          <input
            type="text"
            className="form-input"
            value={newExhibitorName}
            onChange={(e) => setNewExhibitorName(e.target.value)}
            placeholder="Enter exhibitor name"
          />
        </div>

        <div className="form-group">
          <label>Exhibitor Logo <span className="required">*</span></label>
          <div
            className="file-upload-card"
            onClick={() => document.getElementById("exhibitorLogo").click()}
          >
            <div className="file-upload-icon">
              <i className="fas fa-camera" />
            </div>
            <div className="file-upload-content">
              <div className="file-upload-title">Upload Logo</div>
              <div className="file-upload-subtitle">Optional logo image</div>
            </div>
          </div>
          <input
            type="file"
            id="exhibitorLogo"
            name="exhibitorLogo"
            onChange={handleExhibitorLogoChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          {newExhibitorLogo && (
            <div className="file-preview">
              <span>{newExhibitorLogo.name}</span>
              <button
                type="button"
                className="remove-file-btn"
                onClick={() => setNewExhibitorLogo(null)}
              >
                <i className="fas fa-trash" />
              </button>
            </div>
          )}
        </div>

        <div className="form-group"></div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={addExhibitor}
          disabled={!newExhibitorName.trim()}
        >
          <i className="fas fa-plus" /> Add Exhibitor
        </button>
      </div>

      <div className="exhibitor-list">
        <h5>Added Exhibitors ({exhibitorFields.exhibitor_names.length})</h5>
        {exhibitorFields.exhibitor_names.length === 0 ? (
          <p className="empty-message">No exhibitors added yet.</p>
        ) : (
          <div className="exhibitor-items">
            {exhibitorFields.exhibitor_names.map((exhibitor, index) => (
              <div key={index} className="exhibitor-item">
                <div className="exhibitor-info">
                  <span className="exhibitor-name">{exhibitor}</span>
                  <span className="exhibitor-files">
                    {exhibitorFields.exhibitor_logos[index] && (
                      <i className="fas fa-camera" title="Has logo" />
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  className="remove-exhibitor-btn"
                  onClick={() => removeExhibitor(index)}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render Step 1 - Basic Information
  const renderStep1 = () => (
    <div className="form-section" id="basicInfoSection">
      <div className="section-header">
        <h2 className="section-title">
          <i className="fas fa-info-circle" /> Basic Information
        </h2>
        <p className="section-subtitle">Fill in the basic details of your event</p>
      </div>

      <div className="form-grid">
        <div className="form-group full-width">
          <label className="form-label">
            Event Type <span className="required">*</span>
            {usingFallbackData && <span className="form-note">(Using default options)</span>}
          </label>
          <div className="event-types-grid">{renderEventTypeCards()}</div>
          {eventTypesError && <div className="form-error">{eventTypesError}</div>}
          <select
            id="event_type_id"
            name="event_type_id"
            className="form-select"
            value={formData.event_type_id}
            onChange={handleChange}
            required
            style={{ marginTop: "15px" }}
          >
            <option value="">Or select from dropdown...</option>
            {eventTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.type}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="name">
            Event Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="form-input"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter event name"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="venue">
            Venue <span className="required">*</span>
          </label>
          <input
            type="text"
            id="venue"
            name="venue"
            className="form-input"
            value={formData.venue}
            onChange={handleChange}
            required
            placeholder="Event location"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="city">
            City <span className="required">*</span>
          </label>
          <select
            id="city"
            name="city"
            className="form-select"
            value={formData.city}
            onChange={handleChange}
            required
          >
            <option value="">Select city</option>
            {CITIES.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="start_date">
            Start Date & Time <span className="required">*</span>
          </label>
          <div className="datetime-input-container">
            <input
              type="date"
              id="start_date"
              name="start_date"
              className="form-input date-input"
              value={formData.start_date ? formData.start_date.split("T")[0] : ""}
              onChange={(e) => {
                const date = e.target.value;
                const time = formData.start_date?.split("T")[1] || "00:00";
                setFormData({ ...formData, start_date: `${date}T${time}` });
              }}
              required
              min={new Date().toISOString().split("T")[0]}
            />
            <input
              type="time"
              className="form-input time-input"
              value={formData.start_date.split("T")[1] || "00:00"}
              onChange={(e) => {
                const time = e.target.value;
                const date = formData.start_date.split("T")[0] || "";
                setFormData({ ...formData, start_date: `${date}T${time}` });
              }}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="end_date">
            End Date & Time <span className="required">*</span>
          </label>
          <div className="datetime-input-container">
            <input
              type="date"
              id="end_date"
              name="end_date"
              className="form-input date-input"
              value={formData.end_date ? formData.end_date.split("T")[0] : ""}
              onChange={(e) => {
                const date = e.target.value;
                const time = formData.end_date?.split("T")[1] || "00:00";
                setFormData({ ...formData, end_date: `${date}T${time}` });
              }}
              required
              min={new Date().toISOString().split("T")[0]}
            />
            <input
              type="time"
              className="form-input time-input"
              value={formData.end_date.split("T")[1] || "00:00"}
              onChange={(e) => {
                const time = e.target.value;
                const date = formData.end_date.split("T")[0] || "";
                setFormData({ ...formData, end_date: `${date}T${time}` });
              }}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Pricing Type <span className="required">*</span>
          </label>
          <div className="pricing-options">
            <button
              type="button"
              className={`pricing-option-btn ${!formData.is_paid ? "active" : ""}`}
              onClick={() => setFormData({ ...formData, is_paid: false, ticket_price: "" })}
            >
              <i className="fas fa-ticket-alt" /> Free Event
            </button>
            <button
              disabled
              type="button"
              title="The paid event is not active yet."
              className={`pricing-option-btn paid-price ${formData.is_paid ? "active" : ""}`}
              onClick={() => setFormData({ ...formData, is_paid: true })}
            >
              <i className="fas fa-dollar-sign" /> Paid Event
            </button>
          </div>
        </div>

        {formData.is_paid && (
          <div className="form-group">
            <label className="form-label" htmlFor="ticket_price">
              Ticket Price ($) <span className="required">*</span>
            </label>
            <div className="price-input-container">
              <span className="price-prefix">$</span>
              <input
                type="number"
                id="ticket_price"
                name="ticket_price"
                className="form-input price-input"
                min="0"
                step="0.01"
                value={formData.ticket_price}
                onChange={handleChange}
                placeholder="0.00"
                required={formData.is_paid}
              />
            </div>
          </div>
        )}

        <div className="form-group full-width">
          <label className="form-label" htmlFor="description">
            Event Description
          </label>
          <textarea
            id="description"
            name="description"
            className="form-textarea"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            placeholder="Describe your event..."
          />
          <div className="char-counter">{formData.description.length}/1000 characters</div>
        </div>

        {/* Social Media & Contact Fields */}
        {/* Social Media & Contact Fields - 3 Columns */}
        <div className="form-group full-width">
          <div className="social-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>

            <div className="form-group">
              <label className="form-label" htmlFor="organization_name">
                Organizer Name <span className="required">*</span>
              </label>

              <input
                type="text"
                id="organization_name"
                name="organization_name"
                className="form-input"
                value={formData.organization_name || ""}
                onChange={handleChange}
                placeholder="Enter organization name"

              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="contact_email">
                Contact Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                className="form-input"
                value={formData.contact_email || ""}
                onChange={handleChange}
                placeholder="contact@event.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="contact_phone">
                Contact Phone
              </label>

              <input
                type="tel"
                id="contact_phone"
                name="contact_phone"
                className="form-input"
                value={formData.contact_phone || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  handleChange({
                    target: {
                      name: "contact_phone",
                      value: value,
                    },
                  });
                }}
                placeholder="Enter 10 digit phone number"
                maxLength={10}
              />
            </div>



            <div className="form-group">
              <label className="form-label" htmlFor="linkedin_url">
                <i className="fab fa-linkedin" /> LinkedIn
              </label>
              <input
                type="url"
                id="linkedin_url"
                name="linkedin_url"
                className="form-input"
                value={formData.linkedin_url || ""}
                onChange={handleChange}
                placeholder="https://linkedin.com/company/your-event"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="instagram_url">
                <i className="fab fa-instagram" /> Instagram
              </label>
              <input
                type="url"
                id="instagram_url"
                name="instagram_url"
                className="form-input"
                value={formData.instagram_url || ""}
                onChange={handleChange}
                placeholder="https://instagram.com/your-event"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="twitter_url">
                <i className="fab fa-twitter" /> Twitter/X
              </label>
              <input
                type="url"
                id="twitter_url"
                name="twitter_url"
                className="form-input"
                value={formData.twitter_url || ""}
                onChange={handleChange}
                placeholder="https://twitter.com/your-event"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="facebook_url">
                <i className="fab fa-facebook" /> Facebook
              </label>
              <input
                type="url"
                id="facebook_url"
                name="facebook_url"
                className="form-input"
                value={formData.facebook_url || ""}
                onChange={handleChange}
                placeholder="https://facebook.com/your-event"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="website_url">
                <i className="fas fa-globe" /> Website
              </label>
              <input
                type="url"
                id="website_url"
                name="website_url"
                className="form-input"
                value={formData.website_url || ""}
                onChange={handleChange}
                placeholder="https://www.event-website.com"
              />
            </div>
          </div>
        </div>

        {/* Banner and Logo in one row */}
        <div className="form-group full-width">
          <div className="banner-logo-row">
            {/* Event Banner */}
            <div className="form-group banner-group">
              <label className="form-label">Event Banner</label>
              <div
                className="file-upload-card"
                onClick={() => document.getElementById("banner").click()}
              >
                <div className="file-upload-icon">
                  <i className="fas fa-image" />
                </div>
                <div className="file-upload-content">
                  <div className="file-upload-title">Upload Banner</div>
                  <div className="file-upload-subtitle">PNG, JPG, GIF up to 5MB</div>
                </div>
              </div>
              <input
                type="file"
                id="banner"
                name="banner"
                onChange={handleBannerChange}
                accept="image/*"
                style={{ display: "none" }}
              />
              {bannerPreview && (
                <div className="image-preview">
                  <img src={bannerPreview} alt="Banner preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => {
                      setBanner(null);
                      setBannerPreview(null);
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              )}
            </div>

            {/* Event Logo */}
            <div className="form-group logo-group">
              <label className="form-label">Event Logo</label>
              <div
                className="file-upload-card"
                onClick={() => document.getElementById("logo").click()}
              >
                <div className="file-upload-icon">
                  <i className="fas fa-camera" />
                </div>
                <div className="file-upload-content">
                  <div className="file-upload-title">Upload Logo</div>
                  <div className="file-upload-subtitle">PNG, JPG up to 5MB</div>
                </div>
              </div>
              <input
                type="file"
                id="logo"
                name="logo"
                onChange={handleLogoChange}
                accept="image/*"
                style={{ display: "none" }}
              />
              {logoPreview && (
                <div className="image-preview">
                  <img src={logoPreview} alt="Logo preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => {
                      setLogo(null);
                      setLogoPreview(null);
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-outline" onClick={handleCancel}>
          <i className="fas fa-times" /> Cancel
        </button>
        <div className="next-button-wrapper">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNextStep}
          disabled={
            !formData.event_type_id ||
            !formData.name ||
            !formData.start_date ||
            !formData.end_date ||
            !formData.venue ||
            !formData.city ||
            !formData.organization_name?.trim() ||
            !formData.contact_email?.trim()
          }
        >
          Next: Advanced Configuration <i className="fas fa-arrow-right" />
        </button>
           <div className="next-button-tooltip" role="tooltip">
      Fill all required fields to continue.
    </div>
</div>
      </div>
    </div>
  );


  const isTabCompleted = (tab) => {
    switch (tab) {
      case "attendee":
        return (
          enableAttendee &&
          attendeeFields.registration_start_date &&
          attendeeFields.registration_end_date
        );

      case "speaker":
        return (
          enableSpeaker &&
          speakerFields.speaker_start_date &&
          speakerFields.speaker_end_date
        );

      case "sponsor":
        return (
          enableSponsor &&
          sponsorFields.sponsor_names.length > 0
        );

      case "exhibitor":
        return (
          enableExhibitor &&
          exhibitorFields.exhibitor_names.length > 0
        );

      default:
        return false;
    }
  };
  // Render Step 2 - Advanced Configuration
  const renderStep2 = () => (
    <div className="form-section" id="advancedConfigSection">
      <div className="section-header">
        <h2 className="section-title">
          <i className="fas fa-cogs" /> Advanced Configuration
        </h2>
        <p className="section-subtitle">Configure registration and participation options</p>
      </div>





      <div className="config-tabs-wrapper">

        <div className="config-tabs">

          {/* Attendee */}
          <button
            type="button"
            className={`config-tab ${activeTab === "attendee" ? "active" : ""
              } ${isTabCompleted("attendee") ? "completed" : ""}`}
            onClick={() => setActiveTab("attendee")}
          >
            <i className="fas fa-users" />
            <span>Attendee</span>

            {isTabCompleted("attendee") && (
              <span className="config-completed">
                <i className="fas fa-check" />
              </span>
            )}
          </button>

          <span className="config-tab-arrow">
            <i className="fas fa-arrow-right" />
          </span>


          {/* Speaker */}
          <button
            type="button"
            className={`config-tab ${activeTab === "speaker" ? "active" : ""
              } ${isTabCompleted("speaker") ? "completed" : ""}`}
            onClick={() => setActiveTab("speaker")}
          >
            <i className="fas fa-microphone" />
            <span>Speaker</span>

            {isTabCompleted("speaker") && (
              <span className="config-completed">
                <i className="fas fa-check" />
              </span>
            )}
          </button>

          <span className="config-tab-arrow">
            <i className="fas fa-arrow-right" />
          </span>


          {/* Sponsor */}
          <button
            type="button"
            className={`config-tab ${activeTab === "sponsor" ? "active" : ""
              } ${isTabCompleted("sponsor") ? "completed" : ""}`}
            onClick={() => setActiveTab("sponsor")}
          >
            <i className="fas fa-handshake" />
            <span>Sponsor</span>

            {isTabCompleted("sponsor") && (
              <span className="config-completed">
                <i className="fas fa-check" />
              </span>
            )}
          </button>

          <span className="config-tab-arrow">
            <i className="fas fa-arrow-right" />
          </span>


          {/* Exhibitor */}
          <button
            type="button"
            className={`config-tab ${activeTab === "exhibitor" ? "active" : ""
              } ${isTabCompleted("exhibitor") ? "completed" : ""}`}
            onClick={() => setActiveTab("exhibitor")}
          >
            <i className="fas fa-store" />
            <span>Exhibitor</span>

            {isTabCompleted("exhibitor") && (
              <span className="config-completed">
                <i className="fas fa-check" />
              </span>
            )}
          </button>

        </div>








        <div className="config-content">
          {/* Attendee Configuration */}
          {activeTab === "attendee" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-users" /> Enable Attendee Registration
                </div>

                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableAttendee}
                    onChange={(e) => setEnableAttendee(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableAttendee ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>
              </div>

              {enableAttendee && (
                <div className="config-fields">
                  <div className="form-grid compact">
                    <div className="form-group">
                      <label className="form-label">Registration Start Date<span className="required">*</span></label>
                      <input
                        type="date"
                        name="registration_start_date"
                        className="form-input"
                        value={attendeeFields.registration_start_date}
                        onChange={handleAttendeeChange}
                        min={new Date().toISOString().split("T")[0]}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Registration End Date<span className="required">*</span></label>
                      <input
                        type="date"
                        name="registration_end_date"
                        className="form-input"
                        value={attendeeFields.registration_end_date}
                        onChange={handleAttendeeChange}
                        min={new Date().toISOString().split("T")[0]}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Attendee Limit</label>
                      <input
                        type="number"
                        name="registration_limit"
                        className="form-input"
                        min="1"
                        value={attendeeFields.registration_limit}
                        onChange={handleAttendeeChange}
                        placeholder="Unlimited"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Registration Approval</label>
                      <select
                        name="is_auto_approve"
                        className="form-select"
                        value={attendeeFields.is_auto_approve}
                        onChange={(e) =>
                          setAttendeeFields((prev) => ({
                            ...prev,
                            is_auto_approve: e.target.value === "true",
                          }))
                        }
                      >
                        <option value="true">Auto-approve</option>
                        <option value="false">Manual approval</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Registration Form Type</label>
                      <div className="form-type-options">
                        <button
                          type="button"
                          className={`form-type-btn ${attendeeFields.registration_form_type === "default" ? "active" : ""}`}
                          onClick={() => {
                            setActiveTab("attendee");
                            setShowDefaultFormPreview(true);
                          }}
                        >
                          <i className="fas fa-eye" /> Preview Form
                        </button>
                        <button
                          type="button"
                          className={`form-type-btn ${attendeeFields.registration_form_type === "custom" ? "active" : ""}`}
                          onClick={() => {
                            setCustomFormType("attendee");
                            setShowFormBuilderModal(true);
                          }}
                        >
                          <i className="fas fa-pen" /> Custom Form
                        </button>
                      </div>
                      {attendeeFields.registration_form_type === "custom" && (
                        <div className="custom-form-indicator">
                          <i className="fas fa-check-circle"></i>
                          {attendeeFields.custom_form_fields.length} custom field(s) configured
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Speaker Configuration */}
          {activeTab === "speaker" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-microphone" /> Enable Speaker Registration
                </div>


                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableSpeaker}
                    onChange={(e) => setEnableSpeaker(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableSpeaker ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>


              </div>

              {enableSpeaker && (
                <div className="config-fields">
                  <div className="form-grid compact">
                    <div className="form-group">
                      <label className="form-label">CFP Start Date<span className="required">*</span></label>
                      <input
                        type="date"
                        name="speaker_start_date"
                        className="form-input"
                        value={speakerFields.speaker_start_date}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={handleSpeakerChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">CFP End Date<span className="required">*</span></label>
                      <input
                        type="date"
                        name="speaker_end_date"
                        className="form-input"
                        value={speakerFields.speaker_end_date}
                        min={speakerFields.speaker_start_date || new Date().toISOString().split("T")[0]}
                        onChange={handleSpeakerChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Speaker Limit</label>
                      <input
                        type="number"
                        name="speaker_limit"
                        className="form-input"
                        min="1"
                        value={speakerFields.speaker_limit}
                        onChange={handleSpeakerChange}
                        placeholder="Maximum speakers"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Speaker Form Type</label>
                      <div className="form-type-options">
                        <button
                          type="button"
                          className={`form-type-btn ${speakerFields.speaker_form_type === "default" ? "active" : ""}`}
                          onClick={() => {
                            setActiveTab("speaker");
                            setShowDefaultFormPreview(true);
                          }}
                        >
                          <i className="fas fa-eye" /> Preview Form
                        </button>
                        <button
                          type="button"
                          className={`form-type-btn ${speakerFields.speaker_form_type === "custom" ? "active" : ""}`}
                          onClick={() => {
                            setCustomFormType("speaker");
                            setShowFormBuilderModal(true);
                          }}
                        >
                          <i className="fas fa-pen" /> Custom Form
                        </button>
                      </div>
                      {speakerFields.speaker_form_type === "custom" && (
                        <div className="custom-form-indicator">
                          <i className="fas fa-check-circle"></i>
                          {speakerFields.custom_speaker_form.length} custom field(s) configured
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sponsor Configuration */}
          {activeTab === "sponsor" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-handshake" /> Enable Sponsor Registration
                </div>


                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableSponsor}
                    onChange={(e) => setEnableSponsor(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableSponsor ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>




              </div>

              {enableSponsor && (
                <div className="config-fields">{renderSponsorManagement()}</div>
              )}
            </div>
          )}

          {/* Exhibitor Configuration */}
          {activeTab === "exhibitor" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-store" /> Enable Exhibitor Registration
                </div>


                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableExhibitor}
                    onChange={(e) => setEnableExhibitor(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableExhibitor ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>


              </div>

              {enableExhibitor && (
                <div className="config-fields">{renderExhibitorManagement()}</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-outline" onClick={handlePrevStep}>
          <i className="fas fa-arrow-left" /> Back to Basic Info
        </button>
        <button type="button" className="btn btn-primary" onClick={handleNextStep}>
          Next: Preview & Submit <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );

  // Render Step 3 - Preview & Submit
  const renderStep3 = () => (
    <div className="form-section" id="previewSection">
      <div className="section-header">
        <h2 className="section-title">
          <i className="fas fa-eye" /> Preview & Submit
        </h2>
        <p className="section-subtitle">Review your event details before publishing</p>
      </div>

      <div className="preview-container">
        <div className="preview-summary">
          <div className="preview-header">
            <div className="preview-event-type">
              <i className={eventTypes.find((t) => t.id === formData.event_type_id)?.icon || "fas fa-calendar"} />
              <span>{getSelectedEventTypeName()}</span>
            </div>
            <h3 className="preview-event-name">{formData.name || "Untitled Event"}</h3>
          </div>

          <div className="preview-grid">
            <div className="preview-card">
              <h4 className="preview-card-title">
                <i className="fas fa-calendar-alt" /> Event Details
              </h4>
              <div className="preview-detail">
                <span className="preview-label">Date & Time:</span>
                <span className="preview-value">
                  {formData.start_date && formData.end_date
                    ? `${new Date(formData.start_date).toLocaleDateString()} → ${new Date(formData.end_date).toLocaleDateString()}`
                    : "Not set"}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Duration:</span>
                <span className="preview-value">
                  {formData.end_date
                    ? `${Math.round((new Date(formData.end_date) - new Date(formData.start_date)) / (1000 * 60 * 60))} hours`
                    : "Not set"}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Location:</span>
                <span className="preview-value">{formData.venue}, {formData.city}</span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Pricing:</span>
                <span className="preview-value pricing-tag">
                  {formData.is_paid ? `$${formData.ticket_price || "0.00"}` : "Free"}
                </span>
              </div>
            </div>

            <div className="preview-card">
              <h4 className="preview-card-title">
                <i className="fas fa-users" /> Registration Status
              </h4>
              <div className="preview-detail">
                <span className="preview-label">Attendees:</span>
                <span className={`preview-value status-badge ${enableAttendee ? "enabled" : "disabled"}`}>
                  {enableAttendee ? "Enabled" : "Disabled"}
                </span>
              </div>
              {enableAttendee && (
                <>
                  <div className="preview-detail">
                    <span className="preview-label">Limit:</span>
                    <span className="preview-value">{attendeeFields.registration_limit || "Unlimited"}</span>
                  </div>
                  <div className="preview-detail">
                    <span className="preview-label">Approval:</span>
                    <span className="preview-value">
                      {attendeeFields.is_auto_approve ? "Auto-approve" : "Manual approval"}
                    </span>
                  </div>
                  <div className="preview-detail">
                    <span className="preview-label">Form Type:</span>
                    <span className="preview-value">
                      {attendeeFields.registration_form_type}
                      {attendeeFields.registration_form_type === "custom" &&
                        ` (${attendeeFields.custom_form_fields.length} fields)`}
                    </span>
                  </div>
                </>
              )}
              <div className="preview-detail">
                <span className="preview-label">Speakers:</span>
                <span className={`preview-value status-badge ${enableSpeaker ? "enabled" : "disabled"}`}>
                  {enableSpeaker ? "Enabled" : "Disabled"}
                </span>
              </div>
              {enableSpeaker && (
                <div className="preview-detail">
                  <span className="preview-label">Form Type:</span>
                  <span className="preview-value">
                    {speakerFields.speaker_form_type}
                    {speakerFields.speaker_form_type === "custom" &&
                      ` (${speakerFields.custom_speaker_form.length} fields)`}
                  </span>
                </div>
              )}
              <div className="preview-detail">
                <span className="preview-label">Sponsors:</span>
                <span className={`preview-value status-badge ${enableSponsor ? "enabled" : "disabled"}`}>
                  {enableSponsor ? `Enabled (${sponsorFields.sponsor_names.length})` : "Disabled"}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Exhibitors:</span>
                <span className={`preview-value status-badge ${enableExhibitor ? "enabled" : "disabled"}`}>
                  {enableExhibitor ? `Enabled (${exhibitorFields.exhibitor_names.length})` : "Disabled"}
                </span>
              </div>
            </div>

            <div className="preview-card">
              <h4 className="preview-card-title">
                <i className="fas fa-images" /> Media
              </h4>
              <div className="preview-detail">
                <span className="preview-label">Banner:</span>
                <span className="preview-value">{bannerPreview ? "Uploaded" : "Not uploaded"}</span>
              </div>
              {bannerPreview && (
                <div className="preview-image">
                  <img src={bannerPreview} alt="Banner preview" />
                </div>
              )}
              <div className="preview-detail">
                <span className="preview-label">Logo:</span>
                <span className="preview-value">{logoPreview ? "Uploaded" : "Not uploaded"}</span>
              </div>
              {logoPreview && (
                <div className="preview-image">
                  <img src={logoPreview} alt="Logo preview" />
                </div>
              )}
            </div>

            <div className="preview-card full-width">
              <h4 className="preview-card-title">
                <i className="fas fa-align-left" /> Description
              </h4>
              <div className="preview-description">
                {formData.description || "No description provided."}
              </div>
            </div>
          </div>
        </div>

        <div className="terms-box">
          <div className="terms-header">
            <i className="fas fa-file-contract" /> Terms & Conditions
          </div>
          <p className="terms-text">
            By creating this event, you agree to EventFork's Terms of Service
            and confirm that you have the rights to organize this event. All
            events must comply with our community guidelines.
          </p>
          <label className="terms-checkbox">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <span>I accept the terms and conditions</span>
          </label>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-outline" onClick={handlePrevStep}>
          <i className="fas fa-arrow-left" /> Back to Configuration
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-submit"
          disabled={loading || !termsAccepted}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Creating Event...
            </>
          ) : (
            <>
              <i className="fas fa-check" /> Create Event
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="create-event-page">
      <div className="container create-event-container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-plus-circle" /> Create New Event
          </h1>
          <p className="page-subtitle">Create your perfect event in 3 simple steps</p>
        </div>

        <div className="progress-steps1">
          <div className={`progress-step ${currentStep >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <div className="step-info">
              <div className="step-title">Basic Info</div>
              <div className="step-description">Event details</div>
            </div>
          </div>
          <div className={`progress-step ${currentStep >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <div className="step-info">
              <div className="step-title">Configuration</div>
              <div className="step-description">Registration setup</div>
            </div>
          </div>
          <div className={`progress-step ${currentStep >= 3 ? "active" : ""}`}>
            <div className="step-number">3</div>
            <div className="step-info">
              <div className="step-title">Review</div>
              <div className="step-description">Final preview</div>
            </div>
          </div>
        </div>

        <div className="form-card">
          {error && (
            <div className="error-banner">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </form>
        </div>
      </div>

      {/* Default Form Preview Modal */}
      {showDefaultFormPreview && renderDefaultFormPreview()}

      {/* Custom Form Builder Modal */}
      {showFormBuilderModal && (
        <FormBuilderModal
          formType={customFormType}
          onSave={handleFormBuilderSave}
          onClose={() => setShowFormBuilderModal(false)}
        />
      )}
    </div>
  );
};

export default CreateEvent;